import type { ButtonHTMLAttributes, ReactNode } from "react";

// ── Button ──────────────────────────────────────────────────────────────────
type BtnVariant = "primary" | "secondary" | "ghost" | "danger" | "accent";
type BtnSize = "xs" | "sm" | "md" | "lg";

const VARIANT: Record<BtnVariant, string> = {
  primary: "ef-gradient text-white shadow-[0_2px_14px_rgba(0,0,0,0.25)] hover:brightness-110",
  secondary: "bg-ef-panel2 border border-ef-border text-ef-textDim hover:border-ef-accent hover:text-ef-text",
  ghost: "bg-transparent text-ef-textDim hover:text-ef-text",
  danger: "bg-ef-err/[0.08] border border-ef-err/25 text-ef-err hover:bg-ef-err/[0.14]",
  accent: "bg-ef-accent/[0.08] border border-ef-accent/20 text-ef-accent hover:bg-ef-accent/[0.14]",
};

const SIZE: Record<BtnSize, string> = {
  xs: "px-[9px] py-1 text-[11px] gap-1",
  sm: "px-[13px] py-[7px] text-[13px] gap-1.5",
  md: "px-[18px] py-[9px] text-sm gap-2",
  lg: "px-[26px] py-3 text-[15px] gap-2.5",
};

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  icon?: ReactNode;
}

export function Btn({ variant = "secondary", size = "sm", icon, children, className = "", disabled, ...rest }: BtnProps) {
  return (
    <button
      disabled={disabled}
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-[var(--radius)] font-medium transition-all ${SIZE[size]} ${VARIANT[variant]} disabled:pointer-events-none disabled:opacity-45 ${className}`}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

// ── Badge ───────────────────────────────────────────────────────────────────
type BadgeColor = "dim" | "accent" | "purple" | "ok" | "warn" | "err";

const BADGE: Record<BadgeColor, { wrap: string; dot: string }> = {
  dim: { wrap: "bg-ef-textDim/10 text-ef-textMuted", dot: "bg-ef-textMuted" },
  accent: { wrap: "bg-ef-accent/[0.12] text-ef-accent", dot: "bg-ef-accent" },
  purple: { wrap: "bg-ef-accent2/[0.12] text-ef-accent2", dot: "bg-ef-accent2" },
  ok: { wrap: "bg-ef-ok/[0.12] text-ef-ok", dot: "bg-ef-ok" },
  warn: { wrap: "bg-ef-warn/[0.12] text-ef-warn", dot: "bg-ef-warn" },
  err: { wrap: "bg-ef-err/[0.12] text-ef-err", dot: "bg-ef-err" },
};

export function Badge({ children, color = "dim", dot = false }: { children: ReactNode; color?: BadgeColor; dot?: boolean }) {
  const c = BADGE[color];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tracking-wide ${c.wrap}`}
    >
      {dot && <span className={`h-[5px] w-[5px] shrink-0 rounded-full ${c.dot}`} />}
      {children}
    </span>
  );
}

// ── Panel heading (uppercase eyebrow) ─────────────────────────────────────────
export function PanelHead({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ef-textMuted">{children}</span>
      {action}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────
export function ProgressBar({ value, max, height = 4 }: { value: number; max: number; height?: number }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const warn = pct > 80;
  return (
    <div className="overflow-hidden rounded bg-ef-panel3" style={{ height }}>
      <div
        className={`h-full rounded transition-[width] duration-500 ${warn ? "bg-ef-warn" : "ef-gradient"}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ── Stat card (KPI tile with corner glow) ─────────────────────────────────────
// Literal hover classes per color so Tailwind's content scan generates them.
const STAT: Record<"accent" | "purple" | "ok" | "warn",
  { text: string; glow: string; border: string; vHover: string }> = {
  accent: { text: "text-ef-accent", glow: "bg-ef-accent/[0.07]", border: "hover:border-ef-accent/40", vHover: "group-hover:text-ef-accent" },
  purple: { text: "text-ef-accent2", glow: "bg-ef-accent2/[0.07]", border: "hover:border-ef-accent2/40", vHover: "group-hover:text-ef-accent2" },
  ok: { text: "text-ef-ok", glow: "bg-ef-ok/[0.07]", border: "hover:border-ef-ok/40", vHover: "group-hover:text-ef-ok" },
  warn: { text: "text-ef-warn", glow: "bg-ef-warn/[0.07]", border: "hover:border-ef-warn/40", vHover: "group-hover:text-ef-warn" },
};

export function StatCard({
  icon,
  label,
  value,
  sub,
  color = "accent",
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  sub?: string;
  color?: "accent" | "purple" | "ok" | "warn";
}) {
  const c = STAT[color];
  return (
    <div className={`group relative flex flex-col gap-2.5 overflow-hidden rounded-[var(--radius-lg)] border border-ef-border bg-ef-panel px-[18px] py-4 transition-all ${c.border} hover:shadow-[0_4px_24px_rgba(0,0,0,0.25)]`}>
      <div className={`pointer-events-none absolute -right-5 -top-5 h-20 w-20 rounded-full transition-transform duration-200 group-hover:scale-[1.3] ${c.glow}`} />
      <div className="flex items-center justify-between">
        <div className={`flex h-[30px] w-[30px] items-center justify-center rounded-[var(--radius-sm)] transition-transform group-hover:scale-110 ${c.glow} ${c.text}`}>
          {icon}
        </div>
        <span className="text-[11px] font-medium text-ef-textMuted">{label}</span>
      </div>
      <div>
        <div className={`font-display text-[22px] font-bold tracking-[-0.02em] text-ef-text transition-colors ${c.vHover}`}>{value}</div>
        {sub && <div className="mt-0.5 text-[11px] text-ef-textMuted">{sub}</div>}
      </div>
    </div>
  );
}
